<script setup lang="ts">
import type { CVData } from '~/types'

/**
 * "No CV yet? Make one in a short chat" on the tools (ATS check, tailoring,
 * Civil Service, NHS): the homepage chat in a dialog, whose CV then goes into
 * the tool.
 *
 * At the end, before the CV goes on, a visitor without an account is offered
 * what the homepage offers: the CV kept by its link in an e-mail
 * (LandingEmailCvCard, `purpose: 'save'`), with the same unticked marketing
 * consent. The link opens the CV like one made on the homepage. Asked once:
 * sent, skipped or closed, the CV goes on all the same, and a rebuild in the
 * chat goes straight through.
 */
const props = withDefaults(defineProps<{
  modelValue: boolean
  title: string
  /** One line over the chat, desktop only. */
  intro?: string
  /** Label on the saved interview and the guest CV. */
  source: string
}>(), {
  intro: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  /** The CV built in the chat, once the save step is done with. */
  'built': [cv: CVData, generationId: string | null]
}>()

const { t } = useI18n()
const { isAuthenticated } = useAuthUser()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

/** The CV waiting on the save step. */
const pending = ref<{ cv: CVData, generationId: string } | null>(null)
const sent = ref(false)
const asked = ref(false)

function onBuilt(cv: CVData, generationId: string | null) {
  if (isAuthenticated.value || !generationId || asked.value) {
    emit('built', cv, generationId)
    return
  }
  asked.value = true
  sent.value = false
  pending.value = { cv, generationId }
}

function finish() {
  const done = pending.value
  if (!done) return
  pending.value = null
  emit('built', done.cv, done.generationId)
}

// Closed on the save step: the CV goes on, as with "continue without saving".
watch(isOpen, (open) => {
  if (!open) finish()
})
</script>

<template>
  <CommonDialog v-model="isOpen" :title="title" size="4xl" fullscreen-mobile :footer="false">
    <div v-if="pending" class="mx-auto max-w-md py-2">
      <div class="mb-3 flex items-center gap-2 font-title text-xl font-bold text-ink dark:text-white">
        <i class="bi bi-check-circle-fill text-accent" aria-hidden="true" />
        {{ t('landing.emailCv.chatTitle') }}
      </div>
      <LandingEmailCvCard
        gate
        purpose="save"
        :cv-data="pending.cv"
        :generation-id="pending.generationId"
        consent-source="cv-chat"
        @sent="sent = true"
        @unsendable="finish"
      />
      <SofaxBtn v-if="sent" variant="accent" block class="mt-4" :label="t('landing.emailCv.chatContinue')" @click="finish" />
      <p v-else class="mt-4 text-center">
        <button type="button" class="link !text-sm" @click="finish">{{ t('landing.emailCv.chatSkip') }}</button>
      </p>
    </div>
    <template v-else>
      <p v-if="intro" class="mb-4 !text-sm text-muted max-md:hidden dark:text-gray-400">{{ intro }}</p>
      <LandingCvChatBuilder v-if="isOpen" :source="source" :with-heading="false" :ask-posting="false" :with-templates="false" @built="onBuilt" />
    </template>
  </CommonDialog>
</template>
